import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Chip,
  Divider,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  IconButton,
  Tooltip,
  Rating,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Button
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Movie as MovieIcon,
  People as PeopleIcon,
  Visibility,
  Favorite as FavoriteIcon,
  Comment as CommentIcon,
  LocalMovies as LocalMoviesIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp,
  TrendingDown,
  AccessTime,
  ShowChart,
  Notifications,
  InfoOutlined,
  Refresh as RefreshIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, PieChart, Pie, Cell } from 'recharts';

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 调用系统统计API
        const response = await fetch('/api/system/statistics');
        if (!response.ok) {
          throw new Error('Failed to fetch statistics');
        }
        
        const result = await response.json();
        if (result.success) {
          setStatistics(result.data);
        } else {
          throw new Error(result.error || 'Failed to fetch statistics');
        }
      } catch (err) {
        console.error('Error fetching statistics:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  // 数据准备和图表数据生成
  const getChartData = () => {
    if (!statistics) return {};

    // 剧集分类分布
    const categoryData = Object.entries(statistics.category_distribution || {}).map(([category, count]) => ({
      name: category,
      count: count
    })).filter(item => item.count > 0);

    // 用户角色分布
    const roleData = [
      { name: '管理员', value: statistics.user_roles?.admin || 0, color: '#6366f1' },
      { name: '普通用户', value: statistics.user_roles?.user || 0, color: '#8b5cf6' }
    ].filter(item => item.value > 0);

    // 广告性能数据
    const adPerformanceData = [
      { name: '活跃广告', value: statistics.advertisement_performance?.active_ads || 0, color: '#10b981' },
      { name: '非活跃广告', value: statistics.advertisement_performance?.inactive_ads || 0, color: '#f59e0b' }
    ].filter(item => item.value > 0);

    // 热门剧集数据（用于图表）
    const topDramasData = statistics.trending?.top_dramas?.slice(0, 5).map(drama => ({
      name: drama.name.length > 10 ? drama.name.substring(0, 10) + '...' : drama.name,
      views: drama.view_count,
      favorites: drama.favorite_count
    })) || [];

    return {
      categoryData,
      roleData,
      adPerformanceData,
      topDramasData
    };
  };

  const chartData = getChartData();

  // 图表颜色配置 - 现代感配色
  const COLORS = ['#6366f1', '#8b5cf6', '#818cf8', '#a78bfa', '#4f46e5', '#7c3aed'];

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            加载统计数据失败
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            重试
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4, px: isMobile ? 1 : 2 }}>


        {/* 系统概览卡片 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                boxShadow: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)'
                },
                backgroundColor: theme.palette.primary.light
              }}
            >
              <Box sx={{
                mr: 2,
                p: 1.5,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                color: 'white'
              }}>
                <LocalMoviesIcon fontSize="large" />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  短剧数量
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {statistics.overview.total_dramas}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                boxShadow: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)'
                },
                backgroundColor: theme.palette.secondary.light
              }}
            >
              <Box sx={{
                mr: 2,
                p: 1.5,
                borderRadius: '50%',
                backgroundColor: theme.palette.secondary.main,
                color: 'white'
              }}>
                <MovieIcon fontSize="large" />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  剧集数量
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {statistics.overview.total_episodes}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                boxShadow: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)'
                },
                backgroundColor: theme.palette.success.light
              }}
            >
              <Box sx={{
                mr: 2,
                p: 1.5,
                borderRadius: '50%',
                backgroundColor: theme.palette.success.main,
                color: 'white'
              }}>
                <PeopleIcon fontSize="large" />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  用户数量
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {statistics.overview.total_users}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                boxShadow: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)'
                },
                backgroundColor: theme.palette.warning.light
              }}
            >
              <Box sx={{
                mr: 2,
                p: 1.5,
                borderRadius: '50%',
                backgroundColor: theme.palette.warning.main,
                color: 'white'
              }}>
                <CommentIcon fontSize="large" />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  评论数量
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {statistics.overview.total_comments}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                boxShadow: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)'
                },
                backgroundColor: theme.palette.info.light
              }}
            >
              <Box sx={{
                mr: 2,
                p: 1.5,
                borderRadius: '50%',
                backgroundColor: theme.palette.info.main,
                color: 'white'
              }}>
                <Visibility fontSize="large" />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  播放数量
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {statistics.overview.total_views}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                boxShadow: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)'
                },
                backgroundColor: theme.palette.success.light
              }}
            >
              <Box sx={{
                mr: 2,
                p: 1.5,
                borderRadius: '50%',
                backgroundColor: theme.palette.success.main,
                color: 'white'
              }}>
                <FavoriteIcon fontSize="large" />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  收藏数量
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {statistics.overview.total_favorites}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                boxShadow: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)'
                },
                backgroundColor: theme.palette.secondary.light
              }}
            >
              <Box sx={{
                mr: 2,
                p: 1.5,
                borderRadius: '50%',
                backgroundColor: theme.palette.secondary.main,
                color: 'white'
              }}>
                <AttachMoneyIcon fontSize="large" />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  广告数量
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {statistics.overview.total_advertisements}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                boxShadow: 1,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)'
                },
                backgroundColor: theme.palette.error.light
              }}
            >
              <Box sx={{
                mr: 2,
                p: 1.5,
                borderRadius: '50%',
                backgroundColor: theme.palette.error.main,
                color: 'white'
              }}>
                <ShowChart fontSize="large" />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  广告点击
                </Typography>
                <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {statistics.overview.total_ad_clicks}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* 图表区域 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {/* 剧集分类分布图表 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <CardHeader
                title="剧集分类分布"
                subheader="各分类剧集数量统计"
                avatar={
                  <Avatar sx={{ backgroundColor: theme.palette.primary.main }}>
                    <LocalMoviesIcon />
                  </Avatar>
                }
              />
              <CardContent>
                {chartData.categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData.categoryData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {chartData.categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => [`${value}部`, '数量']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography variant="body2" color="text.secondary">
                      暂无分类数据
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Paper>
          </Grid>

          {/* 用户角色分布图表 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <CardHeader
                title="用户角色分布"
                subheader="管理员与普通用户比例"
                avatar={
                  <Avatar sx={{ backgroundColor: theme.palette.secondary.main }}>
                    <PeopleIcon />
                  </Avatar>
                }
              />
              <CardContent>
                {chartData.roleData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData.roleData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name} ${value}人`}
                        outerRadius={100}
                        fill="#82ca9d"
                        dataKey="value"
                      >
                        {chartData.roleData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value) => [`${value}人`, '数量']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography variant="body2" color="text.secondary">
                      暂无用户数据
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Paper>
          </Grid>
        </Grid>

        {/* 广告性能和热门剧集 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {/* 广告性能图表 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <CardHeader
                title="广告性能统计"
                subheader={`点击率: ${statistics.averages.ad_click_rate.toFixed(2)}%`}
                avatar={
                  <Avatar sx={{ backgroundColor: theme.palette.warning.main }}>
                    <AttachMoneyIcon />
                  </Avatar>
                }
              />
              <CardContent>
                {chartData.adPerformanceData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData.adPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip formatter={(value) => [`${value}个`, '数量']} />
                      <Legend />
                      <Bar dataKey="value" fill="#8884d8" name="广告数量" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography variant="body2" color="text.secondary">
                      暂无广告数据
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Paper>
          </Grid>

          {/* 热门剧集图表 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <CardHeader
                title="热门剧集"
                subheader="播放次数排名前5的剧集"
                avatar={
                  <Avatar sx={{ backgroundColor: theme.palette.success.main }}>
                    <TrendingUp />
                  </Avatar>
                }
              />
              <CardContent>
                {chartData.topDramasData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData.topDramasData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip
                        formatter={(value, name) => [
                          `${value}`,
                          name === 'views' ? '播放次数' : '收藏数'
                        ]}
                      />
                      <Legend />
                      <Bar dataKey="views" fill="#1976d2" name="播放次数" />
                      <Bar dataKey="favorites" fill="#dc004e" name="收藏数" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography variant="body2" color="text.secondary">
                      暂无热门剧集数据
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Paper>
          </Grid>
        </Grid>

        {/* 最近添加的剧集和热门评论 */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {/* 最近添加的剧集 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <CardHeader
                title="最近添加的剧集"
                subheader="最新发布的5部剧集"
                avatar={
                  <Avatar sx={{ backgroundColor: theme.palette.primary.main }}>
                    <StarIcon />
                  </Avatar>
                }
              />
              <CardContent>
                {statistics.trending.recent_dramas && statistics.trending.recent_dramas.length > 0 ? (
                  <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {statistics.trending.recent_dramas.slice(0, 5).map((drama) => (
                      <ListItem key={drama.id} alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar sx={{ backgroundColor: theme.palette.primary.light }}>
                            <LocalMoviesIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={drama.name}
                          secondary={
                            <React.Fragment>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                                sx={{ display: 'block', mb: 0.5 }}
                              >
                                {drama.description}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                发布于: {new Date(drama.created_at).toLocaleDateString()}
                              </Typography>
                            </React.Fragment>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={drama.episodes.length > 0 ? `${drama.episodes.length}集` : '无剧集'}
                            size="small"
                            color={drama.episodes.length > 0 ? 'success' : 'default'}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography variant="body2" color="text.secondary">
                      暂无最近添加的剧集
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Paper>
          </Grid>

          {/* 热门评论 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <CardHeader
                title="热门评论"
                subheader="用户互动最多的评论"
                avatar={
                  <Avatar sx={{ backgroundColor: theme.palette.success.main }}>
                    <CommentIcon />
                  </Avatar>
                }
              />
              <CardContent>
                {statistics.trending.top_dramas && statistics.trending.top_dramas.length > 0 ? (
                  <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {statistics.trending.top_dramas.slice(0, 5).map((drama) => (
                      <ListItem key={drama.id} alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar sx={{ backgroundColor: theme.palette.success.light }}>
                            <StarIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={drama.name}
                          secondary={
                            <React.Fragment>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                                sx={{ display: 'block', mb: 0.5 }}
                              >
                                {drama.description}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <Rating value={5} readOnly size="small" />
                                <Typography variant="caption" color="text.secondary">
                                  {drama.view_count} 播放 · {drama.favorite_count} 收藏
                                </Typography>
                              </Box>
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                    <Typography variant="body2" color="text.secondary">
                      暂无热门评论
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AdminDashboard;